import { PATH_SUBSCRIBER } from '@/routes/paths';
import { SUBSCRIBER_PORTAL_DISABLED_CODE } from '@/services/subscriber/enums';
import { parseIdentifier } from '@/utils/refundIdentifier';

/**
 * Sessão do portal do assinante.
 *
 * Guarda o par de tokens que `subscriber/confirm_code/` devolve. É separada da sessão do
 * produtor de propósito: o escopo do assinante vive em claims próprios
 * (`subscriber_scope_type`/`subscriber_scope_value`) e um token não deve valer pelo outro.
 *
 * sessionStorage, não localStorage: a sessão morre ao fechar a aba, que é o comportamento
 * esperado de um portal público acessado por link de e-mail — muitas vezes em máquina
 * compartilhada.
 */

const ACCESS_KEY = 'subscriberAccessToken';
const REFRESH_KEY = 'subscriberRefreshToken';
const IDENTIFIER_KEY = 'subscriberIdentifier';
const IDENTIFIER_TYPE_KEY = 'subscriberIdentifierType';

/**
 * Por onde a sessão entrou. É o que o backend põe em `identifier.type` e o escopo do JWT: o
 * telefone não troca o meio de pagamento nem cancela, e o link do aviso só enxerga uma assinatura
 * e não cancela.
 */
export const SUBSCRIBER_IDENTIFIER_TYPE = {
  EMAIL: 'email',
  PHONE: 'phone',
  LINK: 'link',
};

/** Espelho em memória: sessionStorage falha em modo restrito/anônimo de alguns browsers. */
const memoryFallback = {
  access: '',
  refresh: '',
  identifier: '',
  identifierType: '',
};

function withSessionStorage(action, fallback) {
  try {
    return action(window.sessionStorage);
  } catch (storageUnavailable) {
    return fallback;
  }
}

function read(key, memoryKey) {
  const stored = withSessionStorage((storage) => storage.getItem(key), '');
  return stored || memoryFallback[memoryKey];
}

// Do texto digitado sai só e-mail ou telefone; o link nunca é digitado, vem de quem o trocou.
const typeOfTypedIdentifier = (identifier) => (identifier ? parseIdentifier(identifier).type : '');

export function storeSubscriberSession({ access, refresh, identifier, identifierType } = {}) {
  forgetSubscriberLinkSessionEnded();
  memoryFallback.access = access || '';
  memoryFallback.refresh = refresh || '';
  memoryFallback.identifier = (identifier || '').trim();
  memoryFallback.identifierType =
    identifierType || typeOfTypedIdentifier(memoryFallback.identifier);

  withSessionStorage((storage) => {
    storage.setItem(ACCESS_KEY, memoryFallback.access);
    storage.setItem(REFRESH_KEY, memoryFallback.refresh);
    storage.setItem(IDENTIFIER_KEY, memoryFallback.identifier);
    storage.setItem(IDENTIFIER_TYPE_KEY, memoryFallback.identifierType);
    return null;
  }, null);
}

/** Só o access muda no refresh; o refresh token permanece o mesmo. */
export function updateSubscriberAccessToken(access) {
  memoryFallback.access = access || '';
  withSessionStorage((storage) => storage.setItem(ACCESS_KEY, memoryFallback.access), null);
}

export function readSubscriberAccessToken() {
  return read(ACCESS_KEY, 'access');
}

export function readSubscriberRefreshToken() {
  return read(REFRESH_KEY, 'refresh');
}

/** O identificador é lembrado só para exibição ("código enviado para ..."). */
export function readSubscriberSession() {
  return read(IDENTIFIER_KEY, 'identifier');
}

/**
 * A sessão aberta antes deste campo existir não o tem gravado; ela só podia ter entrado por e-mail
 * ou telefone, e o texto guardado diz qual. Lido do texto, o id da assinatura de uma sessão de link
 * passaria por telefone.
 */
export function readSubscriberIdentifierType() {
  const stored = read(IDENTIFIER_TYPE_KEY, 'identifierType');
  return stored || typeOfTypedIdentifier(readSubscriberSession());
}

export function hasSubscriberSession() {
  return Boolean(readSubscriberAccessToken());
}

export function isSubscriberLinkSession() {
  return (
    hasSubscriberSession() && readSubscriberIdentifierType() === SUBSCRIBER_IDENTIFIER_TYPE.LINK
  );
}

export function clearSubscriberSession() {
  memoryFallback.access = '';
  memoryFallback.refresh = '';
  memoryFallback.identifier = '';
  memoryFallback.identifierType = '';

  withSessionStorage((storage) => {
    storage.removeItem(ACCESS_KEY);
    storage.removeItem(REFRESH_KEY);
    storage.removeItem(IDENTIFIER_KEY);
    storage.removeItem(IDENTIFIER_TYPE_KEY);
    return null;
  }, null);
}

const LINK_SESSION_ENDED_KEY = 'subscriberLinkSessionEnded';

/**
 * A sessão do link do aviso acabou sozinha. Ela não tem refresh, de propósito: com a rotação do
 * refresh, a sessão viveria mais que o link e passaria por cima da validade e da revogação dele. O
 * access dura 2 horas, e o 401 seguinte é o fim. Quem manda para o login é a guarda da tela, que
 * não sabe por que a sessão sumiu; esta marca é o que deixa a entrada explicar.
 *
 * Fora da sessão (sobrevive ao `clearSubscriberSession`), no sessionStorage para durar até a
 * entrada ler, e só lá: perdida num browser sem storage, o login aparece sem o aviso.
 */
export function markSubscriberLinkSessionEnded() {
  withSessionStorage((storage) => storage.setItem(LINK_SESSION_ENDED_KEY, '1'), null);
}

export function hasSubscriberLinkSessionEnded() {
  return withSessionStorage((storage) => storage.getItem(LINK_SESSION_ENDED_KEY) === '1', false);
}

export function forgetSubscriberLinkSessionEnded() {
  withSessionStorage((storage) => storage.removeItem(LINK_SESSION_ENDED_KEY), null);
}

const OTP_LOCKS_KEY = 'subscriberOtpLocks';

/**
 * Bloqueios do código de acesso, como a tela os viu: `{ [identificador normalizado]: fim do prazo
 * em ms }`. Um por identificador, porque o backend bloqueia cada um separado, e uma vaga só
 * esqueceria o primeiro ao bloquear o segundo.
 *
 * Durante o bloqueio o `get_token` responde o mesmo 200 de um envio e não envia nada, e o prazo
 * só vem no texto do `otp_locked`. Este registro é o que impede a tela de prometer um código que
 * não sai. Fica no sessionStorage para sobreviver ao reload e à volta do login com Google, e fica
 * fora da sessão: sair da conta não destrava nada no backend.
 */
export function storeSubscriberOtpLocks(locks) {
  const hasLocks = Object.keys(locks || {}).length > 0;
  withSessionStorage((storage) => {
    if (hasLocks) storage.setItem(OTP_LOCKS_KEY, JSON.stringify(locks));
    else storage.removeItem(OTP_LOCKS_KEY);
    return null;
  }, null);
}

export function readSubscriberOtpLocks() {
  const stored = withSessionStorage((storage) => storage.getItem(OTP_LOCKS_KEY), null);
  if (!stored) return {};

  try {
    const locks = JSON.parse(stored);
    if (!locks || typeof locks !== 'object' || Array.isArray(locks)) return {};
    return Object.fromEntries(Object.entries(locks).filter(([, until]) => Number.isFinite(until)));
  } catch (malformed) {
    return {};
  }
}

export function isSubscriberPortalDisabledError(error) {
  return (
    error?.response?.status === 503 &&
    error.response.data?.code === SUBSCRIBER_PORTAL_DISABLED_CODE
  );
}

/**
 * O portal foi desligado com o assinante dentro: encerra a sessão e recarrega a tela inicial,
 * que lê a switch de novo e mostra "Em Breve".
 *
 * Recarregar, e não navegar pelo router: quem chama é o cliente HTTP, fora da árvore React, e o
 * cache do React Query ainda guarda o `enabled: true` lido na entrada, que mostraria o formulário.
 * A sessão sai antes para a tela inicial não mandar o assinante de volta para dentro.
 */
export function leaveDisabledSubscriberPortal() {
  clearSubscriberSession();
  window.location.replace(PATH_SUBSCRIBER.root);
}

/**
 * A rota é do portal do assinante. O do produtor também tem assinaturas, mas em
 * `/dashboard/subscriptions`, e um `startsWith` cru casaria `/subscriptionsX`.
 */
export function isSubscriberPortalPath(pathname) {
  const path = pathname || '';
  return path === PATH_SUBSCRIBER.root || path.startsWith(`${PATH_SUBSCRIBER.root}/`);
}

/** Destino interno da área do assinante: o único que a entrada aceita seguir depois do login. */
export function isSubscriberRedirectTarget(next) {
  const candidate = (next || '').trim();
  return candidate.startsWith(`${PATH_SUBSCRIBER.root}/`) && !candidate.startsWith('//');
}

/** Só aceita destino interno da área do assinante; qualquer outro cai na listagem. */
export function resolveSubscriberRedirect(next) {
  return isSubscriberRedirectTarget(next) ? (next || '').trim() : PATH_SUBSCRIBER.list;
}
